import React, { useState, useEffect } from "react"; 
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider, 
  signInWithPopup,
  onAuthStateChanged 
} from "firebase/auth";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore"; 
import { auth, db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react"; // Eye icon-க்காக சேர்க்கப்பட்டது
import EduConnectLogo from "./logo.png";
import GoogleIcon from "./gl.webp";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPwd] = useState("");
  const [showPassword, setShowPassword] = useState(false); // Password-ஐ காட்ட/மறைக்க state
  const [error, setError] = useState("");
  const [success, setSucc] = useState("");
  const [role, setRole] = useState("public_user"); 
  const [socialRole, setSocialRole] = useState("public_user"); 
  
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const navigate = useNavigate();

  // ---------------------------------------------------------
  // ✅ UPDATED AUTO-LOGIN CHECK (Handles Full Email & Unique Usernames)
  // ---------------------------------------------------------
  // ---------------------------------------------------------
  // ✅ UPDATED AUTO-LOGIN CHECK (No issues for old users)
  // ---------------------------------------------------------
useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        if (currentUser.email === "banupriya@avaami.com") {
           navigate("/adminhandle");
           return;
        } 

        const fullEmailId = currentUser.email;
        const uniqueUsername = currentUser.email.replace(/[^a-zA-Z0-9]/g, '');
        const oldUsername = currentUser.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');

        // Ella snap-aiyum parallel-aa edukkirom
        const [fullEmailSnap, uniqueSnap, oldSnap] = await Promise.all([
          getDoc(doc(db, "profiles", fullEmailId)),
          getDoc(doc(db, "profiles", uniqueUsername)),
          getDoc(doc(db, "profiles", oldUsername))
        ]);

        let profileFound = false;

        // 1. Full Email check (Highest Priority)
        if (fullEmailSnap.exists()) {
          profileFound = true;
        } 
        // 2. Unique Username check
        else if (uniqueSnap.exists()) {
          profileFound = true;
        } 
        // 3. Old Username check (Backward compatibility with safety check)
        else if (oldSnap.exists()) {
          const profileData = oldSnap.data();
          // Profile-il irukkum email, ippo login panna email-um onnanu paarkkum
          // 'contact@pvr.com' login panna, 'contact@emarsilks.com' email match aagathu.
          if (profileData.email === currentUser.email) {
            profileFound = true;
          }
        }

        // Final Navigation
        if (profileFound) {
           navigate("/home"); 
        } else {
           // 'contact@pvr.com' profile create pannala na, ippo inga thaan varum
           navigate("/createprofile"); 
        }
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const getError = (err) =>
    err?.message?.replace("Firebase:", "") || err.toString();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSucc("");

    try {
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      const user = userCred.user;

      if (user.email === "banupriya@avaami.com") {
        setSucc("✅ Welcome Admin!");
        setTimeout(() => navigate("/adminhandle"), 1200);
        return;
      }

      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);

      if (!snap.exists()) {
        setError("User not found. Please register.");
        return;
      }

      if (snap.data().userRole !== role) {
        setError(`You are registered as ${snap.data().userRole.replace("_", " ")}`);
        return;
      }

      await updateDoc(userRef, {
        status: "logged_in",
        lastLogin: new Date(),
      });

      setSucc("✅ Login successful!");
    } catch (err) {
      setError(getError(err));
    }
  };

  const handleSendResetEmail = async (e) => {
    e.preventDefault();
    setError("");
    setSucc("");
    if (!forgotEmail) {
        setError("Please enter your email address.");
        return;
    }
    try {
      await sendPasswordResetEmail(auth, forgotEmail.trim());
      setSucc("✅ Password reset link sent to your email!");
      setShowForgotModal(false); 
      setForgotEmail(""); 
    } catch (err) {
      setError(getError(err));
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setSucc("");
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      if (user.email === "banupriya@avaami.com") {
        setSucc("✅ Welcome Admin! Redirecting...");
        setTimeout(() => navigate("/adminhandle"), 1200);
        return;
      }

      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);

      if (!snap.exists()) {
        const fullName = user.displayName || "User";
        await setDoc(userRef, {
          firstName: fullName.split(" ")[0],
          email: user.email,
          userRole: socialRole,
          createdAt: new Date(),
          status: "logged_in",
          lastLogin: new Date(),
        });
        setSucc(`✅ Google Login Successful!`);
      } else {
        if (snap.data().userRole !== socialRole) {
          setError(`You are already registered as a ${snap.data().userRole.replace("_", " ")}.`);
          return;
        }
        await updateDoc(userRef, {
          status: "logged_in",
          lastLogin: new Date(),
        });
        setSucc("✅ Google Login successful!");
      }

    } catch (err) {
      console.error("Google Login Error:", err);
      setError(getError(err));
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img src={EduConnectLogo} alt="logo" style={styles.logo} />
        <h2 style={styles.title}>Login to Your Account</h2>

        <form onSubmit={handleLogin}>
          <label style={styles.label}>Email Address</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email Address"
            style={styles.input}
          />

          <label style={styles.label}>Password</label>
          {/* Password box with Eye Icon */}
          <div style={{ position: "relative", width: "100%", marginBottom: 16 }}>
            <input
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Password"
              style={{ 
                ...styles.input, 
                marginBottom: 0, 
                paddingRight: "45px",
                boxSizing: "border-box" 
              }}
            />
            <span 
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                color: "#666",
                zIndex: 10
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </span>
          </div>
          
          <div style={{ textAlign: "right", marginBottom: 12, marginTop: -8 }}>
            <span 
              onClick={() => { setError(""); setSucc(""); setShowForgotModal(true); }} 
              style={styles.link}
            >
              Forgot Password?
            </span>
          </div>

          <button type="submit" style={styles.button}>Login</button>
        </form>
        
        <p style={styles.orText}>OR</p>
        
        <button onClick={handleGoogleLogin} style={styles.googleButton}>
          <img src={GoogleIcon} alt="Google logo" style={styles.googleIcon} />
          Continue with Google
        </button>

        <p style={styles.registerText}>
          Don’t have an account?
          <span style={{...styles.link, fontSize: 14}} onClick={() => navigate("/register")}>
            {" "}Click here to Create an Account
          </span>
        </p>

        {error && <p style={styles.error}>❌ {error}</p>}
        {success && <p style={styles.success}>✅ {success}</p>}
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Reset Your Password</h3>
            <p style={{marginBottom: 15, fontSize: 14}}>Enter your registered email address to receive a password reset link.</p>
            <form onSubmit={handleSendResetEmail}>
                <label style={styles.label}>Email</label>
                <input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter email to reset"
                    style={styles.input}
                />
                <button type="submit" style={{...styles.button, marginBottom: 10, marginTop: 10}}>Send Reset Link</button>
                <button type="button" onClick={() => setShowForgotModal(false)} style={{...styles.button, background: '#888', marginBottom: 0}}>Cancel</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;

const styles = {
  page: { minHeight: "100vh", background: "#f4f7ff", display: "flex", justifyContent: "center", alignItems: "center" },
  card: { width: 380, padding: 30, borderRadius: 18, boxShadow: "0 6px 20px rgba(0,0,0,0.08)", background: "#fff" },
  logo: { width: 90, margin: "0 auto 10px", display: "block" },
  title: { textAlign: "center", color: "#203a8f", marginBottom: 20, fontWeight: "700" },
  label: { display: "block", fontWeight: "600", marginBottom: 6, color: "#222" },
  input: { width: "100%", padding: 12, borderRadius: 10, border: "1px solid #ccc", marginBottom: 16, fontSize: 15, outline: "none" },
  button: { width: "100%", padding: 14, borderRadius: 12, background: "#213d8f", color: "#fff", border: "none", fontSize: 16, cursor: "pointer", fontWeight: "bold", marginTop: 0, marginBottom: 16 },
  orText: { textAlign: "center", margin: "12px 0", color: "#666", fontSize: 14, fontWeight: 500 },
  googleButton: { width: "100%", padding: "10px 14px", borderRadius: 8, background: "#fff", color: "#424242", border: "1px solid #ccc", fontSize: 16, cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 },
  googleIcon: { width: 20, height: 20, marginRight: 10 },
  link: { color: "#1d4ed8", cursor: "pointer", fontWeight: 600 },
  registerText: { textAlign: "center", marginTop: 16, fontSize: 14 },
  error: { color: "red", textAlign: "center", marginTop: 10 },
  success: { color: "green", textAlign: "center", marginTop: 10 },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modalContent: { background: '#fff', padding: 30, borderRadius: 12, width: 350, boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)' },
  modalTitle: { color: "#203a8f", marginBottom: 10, fontWeight: "700" }
};